import { useNavigate } from 'react-router-dom';
import type { RecentActivityItem } from '@/services/dashboard.service';
import { ROUTES } from '@/routes/routes';

const STATUS_BADGE: Record<string, string> = {
  SCHEDULED: 'bg-sky-500/10 text-sky-700',
  ASSIGNED: 'bg-blue-500/10 text-blue-700',
  IN_PROGRESS: 'bg-amber-500/10 text-amber-700',
  COMPLETED: 'bg-teal-500/10 text-teal-700',
  REVIEWED: 'bg-purple-500/10 text-purple-700',
  CLOSED: 'bg-green-500/10 text-green-700',
  CANCELLED: 'bg-red-500/10 text-red-700',
};

const STATUS_LABEL: Record<string, string> = {
  SCHEDULED: 'Scheduled',
  ASSIGNED: 'Assigned',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  REVIEWED: 'Reviewed',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface RecentActivityFeedProps {
  items: RecentActivityItem[];
}

export function RecentActivityFeed({ items }: RecentActivityFeedProps) {
  const navigate = useNavigate();

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="px-5 py-3.5 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">Recent Activity</h3>
      </div>

      {items.length === 0 ? (
        <p className="px-5 py-6 text-sm text-muted-foreground text-center">No recent activity.</p>
      ) : (
        <ul className="divide-y divide-border">
          {items.map((item) => (
            <li
              key={item.inspectionId}
              className="flex items-center justify-between gap-4 px-5 py-3.5 cursor-pointer transition-colors hover:bg-muted/30"
              onClick={() => navigate(ROUTES.INSPECTION_DETAIL.replace(':id', item.inspectionId))}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-primary truncate">{item.inspectionNumber}</p>
                <p className="mt-0.5 text-xs text-muted-foreground truncate">
                  {[item.customerOrganizationName ?? item.customerName, item.equipmentName]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[item.status] ?? 'bg-muted text-muted-foreground'}`}
                >
                  {STATUS_LABEL[item.status] ?? item.status}
                </span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {timeAgo(item.updatedAt)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function RecentActivitySkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="px-5 py-3.5 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">Recent Activity</h3>
      </div>
      <ul className="divide-y divide-border">
        {Array.from({ length: 5 }).map((_, i) => (
          <li key={i} className="flex items-center justify-between gap-4 px-5 py-3.5">
            <div className="space-y-1.5 flex-1">
              <div className="h-4 w-28 rounded skeleton-shimmer" />
              <div className="h-3 w-44 rounded skeleton-shimmer" />
            </div>
            <div className="h-5 w-16 rounded-full skeleton-shimmer" />
          </li>
        ))}
      </ul>
    </div>
  );
}
